import { bootstrapAbi, CALL_TYPE, entryPointAbi, EXEC_TYPE, factoryAbi, getExecuteMode } from '../common';
import { encodeFunctionData, parseAbi, encodeAbiParameters, parseAbiParameters, type WalletClient, type PublicClient, toBytes, concat, getAddress, pad, toHex, isBytes, Account, Hex } from 'viem';
import { accountAbi } from '../common';
import { BigNumber, BigNumberish } from '../types/bignumber';
import { BaseAccountAPI, BaseApiParams } from './BaseAccountAPI';
import { BootstrapConfig, makeBootstrapConfig, _makeBootstrapConfig } from './Bootstrap';
import { Networks, DEFAULT_MULTIPLE_OWNER_ECDSA_VALIDATOR_ADDRESS } from '../network/constants';

const ADDRESS_ZERO = getAddress("0x0000000000000000000000000000000000000000");

/**
 * constructor params, added no top of base params:
 * @param owner the signer object for the account owner
 * @param factoryAddress address of contract "factory" to deploy new contracts (not needed if account already deployed)
 * @param index nonce value used when creating multiple accounts for the same owner
 */
export interface EtherspotWalletApiParams extends BaseApiParams {
  factoryAddress?: string;
  index?: number;
  chainId: number;
  etherspotWalletAddress?: string;
}

/**
 * An implementation of the BaseAccountAPI using the EtherspotWallet contract.
 * - contract deployer gets "entrypoint", "owner" addresses and "index" nonce
 * - owner signs requests using normal "Ethereum Signed Message" (ether's signer.signMessage())
 * - nonce method is "nonce()"
 * - execute method is "execFromEntryPoint()"
 */
export class EtherspotWalletAPI extends BaseAccountAPI {
  index: number;
  accountAddress?: string;
  bootstrapAddress?: string;
  multipleOwnerECDSAValidatorAddress?: string;
  eoaAddress: Hex;

  constructor(params: EtherspotWalletApiParams) {
    super(params);
    this.index = params.index ?? 0;
    this.multipleOwnerECDSAValidatorAddress = Networks[params.chainId]?.contracts?.multipleOwnerECDSAValidator ?? DEFAULT_MULTIPLE_OWNER_ECDSA_VALIDATOR_ADDRESS;
  }

  getEOAAddress(): Hex {
    return this.externalViemAccount.address
  }

  /**
 * return the value to put into the "initCode" field, if the account is not yet deployed.
 * this value holds the "factory" address, followed by this account's information
 */
  async getAccountInitCode(): Promise<string> {
    if (this.factoryAddress == null || this.factoryAddress == '') {
      throw new Error('no factory to get initCode');
    }

    const initCode = await this.getInitCodeData();
    const salt = pad(toHex(this.index), { size: 32 });

    const functionData = encodeFunctionData({
      functionName: 'createAccount',
      abi: parseAbi(factoryAbi),
      args: [
        salt,
        initCode,
      ],
    })

    return concat([
      this.factoryAddress as Hex,
      functionData,
    ]);
  }

  async getInitCodeData(): Promise<string> {
    const validators: BootstrapConfig[] = makeBootstrapConfig(this.multipleOwnerECDSAValidatorAddress, '0x');
    const executors: BootstrapConfig[] = makeBootstrapConfig(ADDRESS_ZERO, '0x');
    const hook: BootstrapConfig = _makeBootstrapConfig(ADDRESS_ZERO, '0x');
    const fallbacks: BootstrapConfig[] = makeBootstrapConfig(ADDRESS_ZERO, '0x');

    const initMSAData = encodeFunctionData({
      functionName: 'initMSA',
      abi: parseAbi(bootstrapAbi),
      args: [validators, executors, hook, fallbacks],
    });
    const eoaAddress = await this.getEOAAddress();

    const initCode = encodeAbiParameters(
      parseAbiParameters('address, address, bytes'),
      [eoaAddress, this.bootstrapAddress as Hex, initMSAData]
    )

    return initCode;
  }

  async getNonce(key: BigNumber = BigNumber.from(0)): Promise<BigNumber> {
    const etherspotWalletAddress = await this.getEtherspotWalletAddress();
    const dummyKey = key.eq(0)
      ? getAddress(this.multipleOwnerECDSAValidatorAddress) + "00000000"
      : getAddress(key.toHexString()) + "00000000";

    const nonceResponse = await this.publicClient.readContract({
      address: this.entryPointAddress as Hex,
      abi: parseAbi(entryPointAbi),
      functionName: 'getNonce',
      args: [etherspotWalletAddress, BigInt(dummyKey)]
    });
    return nonceResponse as BigNumber;
  }


  /**
   * encode a method call from entryPoint to our contract
   * @param target
   * @param value
   * @param data
   */
  async encodeExecute(target: string, value: BigNumberish, data: string): Promise<string> {
    const executeMode = getExecuteMode({
      callType: CALL_TYPE.SINGLE,
      execType: EXEC_TYPE.DEFAULT
    });

    // Assuming toHex is a function that accepts string | number | bigint | boolean | Uint8Array
    // Convert BigNumberish to a string if it's a BigNumber
    // Convert BigNumberish or Bytes to a compatible type
    let valueToProcess: string | number | bigint | boolean | Uint8Array;

    if (BigNumber.isBigNumber(value)) {
      valueToProcess = value.toString(); // Convert BigNumber to string
    } else if (isBytes(value)) {
      valueToProcess = new Uint8Array(value); // Convert Bytes to Uint8Array
    } else {
      // Here, TypeScript is unsure about the type of `value`
      // You need to ensure `value` is of a type compatible with `valueToProcess`
      // If `value` can only be string, number, bigint, boolean, or Uint8Array, this assignment is safe
      // If `value` can be of other types (like Bytes), you need an explicit conversion or handling here
      // For example, if there's a chance `value` is still `Bytes`, you could handle it like so:
      if (typeof value === 'object' && value !== null && 'length' in value) {
        // Assuming this condition is sufficient to identify Bytes-like objects
        // Convert it to Uint8Array
        valueToProcess = new Uint8Array(Object.values(value));
      } else {
        valueToProcess = value as string | number | bigint | boolean | Uint8Array;
      }
    }

    const calldata = concat([
      target as Hex,
      pad(toHex(valueToProcess), { size: 32 }) as Hex,
      data as Hex
    ]);

    return encodeFunctionData({
      functionName: 'execute',
      abi: parseAbi(accountAbi),
      args: [executeMode, calldata],
    });
  }

  async encodeBatch(targets: string[], values: BigNumberish[], datas: string[]): Promise<string> {

    const executeMode = getExecuteMode({
      callType: CALL_TYPE.BATCH,
      execType: EXEC_TYPE.DEFAULT
    });

    const result = targets.map((target, index) => ({
      target: target as Hex,
      value: values[index],
      callData: datas[index] as Hex
    }));

    const convertedResult = result.map(item => ({
      ...item,
      // Convert `value` from BigNumberish to bigint
      value: typeof item.value === 'bigint' ? item.value : BigInt(item.value.toString()),
    }));

    //TODO-Test-LibraryFix identify the syntax for viem to pass array of tuple
    // const calldata = ethers.utils.defaultAbiCoder.encode(
    //   ["tuple(address target,uint256 value,bytes callData)[]"],
    //   [result]
    // );

    const calldata = encodeAbiParameters(
      parseAbiParameters('(address target,uint256 value,bytes callData)[]'),
      [convertedResult]
    )

    return encodeFunctionData({
      functionName: 'execute',
      abi: parseAbi(accountAbi),
      args: [executeMode, calldata],
    });
  }

}
